// Shared store map renderer. Each store's map file supplies its background drawing
// (an <svg viewBox> component), the drawing's size, and where each zone's number goes.
//
// Sizing: the map keeps its drawing's proportions and fits BOTH the available width and
// `maxHeight`, so wide maps fill the width and tall maps (like a portrait floor plan) get
// narrower instead of taller. The numbers are drawn inside the SVG, in the drawing's own
// coordinates, so they stay on their zones at any size.
//
// Number position, per zone: `labelPosition: { x, y }` in locations.json if set; otherwise
// a point near the zone's centre that sits comfortably inside its shape (see
// labelPlacement.js; worked out from the drawn outline); otherwise
// (before the shapes are measured) the store's `labelPoint`, by default the box centre.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import Icon from '../../../components/atoms/Icon.js';
import { zoneHeatColor, HEAT_GRADIENT } from '../retailPalette';
import { labelPoint as balancedPoint, outlineOf } from './labelPlacement';

const HOVER_FILL = '#02a693';

// Default number position: centre of the zone's box from locations.json
export const boxCenter = (zone) => ({
  x: zone.x + (zone.width || 0) / 2,
  y: zone.y + (zone.height || 0) / 2,
});

const ZoneShape = ({ zone, fill, onEnter, onLeave }) => {
  const props = {
    id: `zone-${zone.id}`,
    'data-zone-id': zone.id,
    fill,
    style: { transition: 'fill 0.2s ease-in-out', cursor: 'pointer' },
    onMouseEnter: (e) => onEnter(e, zone),
    onMouseLeave: onLeave,
  };
  const shape = zone.shape || {};
  if (shape.type === 'path') return <path {...props} d={shape.d} />;
  if (shape.type === 'polygon') return <polygon {...props} points={(shape.points || []).join(' ')} />;
  return <rect {...props} x={shape.x} y={shape.y} width={shape.width} height={shape.height} />;
};

export default function StoreMapCanvas({
  Background,
  width,
  height,
  currentLocation,
  currentData,
  findLabelByZoneId,
  isAnalyticsView,
  hoveredZoneId,
  onZoneHover,
  labelPoint = boxCenter,
  labelSize,
  maxHeight = 560,
  availableHeight = null,
  extraSvg = null,
  tooltipExtra = null,
}) {
  const [hovered, setHovered] = useState(null);
  const [tipPos, setTipPos] = useState({ x: 0, y: 0 });
  const boxRef = useRef(null);
  const zones = currentLocation?.zones || [];

  // Measure each zone's outline once per set of shapes and find its best number spot
  const [autoPoints, setAutoPoints] = useState({});
  const shapesKey = zones.map((z) => `${z.id}:${z.shape?.d || ''}${(z.shape?.points || []).join(',')}${z.shape?.x ?? ''}`).join('|');
  useLayoutEffect(() => {
    const root = boxRef.current;
    if (!root) return;
    const found = {};
    zones.forEach((zone) => {
      const el = root.querySelector(`[data-zone-id="${zone.id}"]`);
      const p = balancedPoint(outlineOf(el));
      if (p) found[zone.id] = p;
    });
    setAutoPoints(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shapesKey]);

  const numberPoint = (zone) => {
    if (zone.labelPosition && typeof zone.labelPosition.x === 'number') return zone.labelPosition;
    return autoPoints[zone.id] || labelPoint(zone);
  };

  // Hovering a zone in the list highlights it on the map
  useEffect(() => {
    if (hoveredZoneId === undefined) return;
    setHovered(hoveredZoneId === null ? null : zones.find((z) => z.id === hoveredZoneId) || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredZoneId]);

  const traffic = (id) => currentData?.traffic?.find((t) => t.id === id)?.traffic || 0;
  const allTraffic = (currentData?.traffic || []).map((t) => t.traffic || 0);
  const heat = (id) => zoneHeatColor(traffic(id), allTraffic, isAnalyticsView);

  // Tooltip next to the zone, kept inside the map
  const onEnter = (e, zone) => {
    const box = boxRef.current?.getBoundingClientRect();
    const r = e.currentTarget.getBoundingClientRect();
    if (box && box.width && box.height) {
      let x = ((r.left + r.width / 2 - box.left) / box.width) * 100 + 6;
      let y = ((r.top + r.height / 2 - box.top) / box.height) * 100 + 4;
      if (x > 68) x -= 34;
      if (y > 68) y -= 30;
      setTipPos({ x: Math.max(2, x), y: Math.max(2, y) });
    }
    setHovered(zone);
    if (onZoneHover) onZoneHover(zone.id);
  };
  const onLeave = () => {
    setHovered(null);
    if (onZoneHover) onZoneHover(null);
  };

  // Tallest the map may be: the space the page has for it (live view), else the store's max
  const fitHeight = availableHeight || maxHeight;

  // Number size in drawing units, so it scales with the map
  const fontSize = labelSize || Math.max(width, height) * 0.03;

  return (
    <div className="rx-store-map">
      <div
        ref={boxRef}
        className="rx-store-map-box"
        style={{
          aspectRatio: `${width} / ${height}`,
          width: `min(100%, ${(fitHeight * width) / height}px)`,
        }}
        onPointerLeave={onLeave}
      >
        <Background>
          {zones.map((zone) => (
            <ZoneShape
              key={zone.id}
              zone={zone}
              fill={zone.id === hovered?.id ? HOVER_FILL : heat(zone.id)}
              onEnter={onEnter}
              onLeave={onLeave}
            />
          ))}
          {!isAnalyticsView && zones.map((zone) => {
            const p = numberPoint(zone);
            if (!p || Number.isNaN(p.x) || Number.isNaN(p.y)) return null;
            return (
              <text
                key={`label-${zone.id}`}
                data-label-for={zone.id}
                x={p.x}
                y={p.y}
                textAnchor="middle"
                dominantBaseline="central"
                className="rx-store-map-label"
                style={{ fontSize }}
              >
                {traffic(zone.id)}
              </text>
            );
          })}
          {extraSvg}
        </Background>

        {hovered && !isAnalyticsView && (
          <div className="rx-store-map-tip" style={{ left: `${tipPos.x}%`, top: `${tipPos.y}%` }}>
            {tooltipExtra ? tooltipExtra(hovered) : null}
            <div className="rx-store-map-tip-zone">Zone {hovered.id}</div>
            <div className="rx-store-map-tip-name">{findLabelByZoneId ? findLabelByZoneId(hovered.id) : hovered.label}</div>
            <div className="rx-store-map-tip-value">
              {traffic(hovered.id)} <Icon name="person-standing" />
            </div>
            <div className="rx-store-map-tip-bar" style={{ background: heat(hovered.id) }} />
          </div>
        )}
      </div>

      <div className="rx-store-map-legend">
        <span>{isAnalyticsView ? 'Avg occupancy' : 'Occupancy'}</span>
        <span className="rx-store-map-legend-low">Low</span>
        <span className="rx-store-map-legend-bar" style={{ background: HEAT_GRADIENT }} />
        <span>High</span>
      </div>
    </div>
  );
}
